import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import { 
  FileQuestion, 
  Plus, 
  Loader2, 
  CheckCircle, 
  AlertTriangle,
  Trash2,
  Edit,
  Sparkles,
  Clock,
  Target
} from 'lucide-react';

const API = (window.location.origin || '') + '/api';

const AdminMockExamsTab = ({ getAuthHeaders }) => {
  const [mockExams, setMockExams] = useState([]);
  const [tracksSummary, setTracksSummary] = useState({});
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState({});
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingExam, setEditingExam] = useState(null);

  const [formData, setFormData] = useState({
    track: 'PMP',
    name: '',
    description: '',
    question_count: 50,
    time_minutes: 60,
    passing_score: 61,
    is_active: true
  });

  const tracks = [
    { code: 'PMP', name: 'PMP', defaults: { questions: 180, time: 230, passing: 61 } },
    { code: 'CAPM', name: 'CAPM', defaults: { questions: 150, time: 180, passing: 61 } },
    { code: 'PSM', name: 'Scrum Master', defaults: { questions: 80, time: 60, passing: 85 } },
    { code: 'PSPO', name: 'Product Owner', defaults: { questions: 80, time: 60, passing: 85 } }
  ];

  useEffect(() => {
    fetchMockExams();
  }, []);

  const fetchMockExams = async () => {
    try {
      const response = await fetch(`${API}/admin/mock-exams`, {
        credentials: 'include',
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setMockExams(data.mock_exams || []);
        setTracksSummary(data.tracks_summary || {});
      }
    } catch (error) {
      console.error('Failed to fetch mock exams:', error);
      toast.error('Failed to load mock exams');
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateExams = async (track, count = 3) => {
    setGenerating(prev => ({ ...prev, [track]: true }));
    
    try {
      const response = await fetch(`${API}/admin/mock-exams/generate/${track}?count=${count}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders()
        },
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        toast.success(data.message);
        fetchMockExams();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to generate exams');
      }
    } catch (error) {
      toast.error('Failed to generate exams');
    } finally {
      setGenerating(prev => ({ ...prev, [track]: false }));
    }
  };

  const handleCreateExam = async () => {
    try {
      const endpoint = editingExam 
        ? `${API}/admin/mock-exams/${editingExam.mock_exam_id}`
        : `${API}/admin/mock-exams`;
      
      const method = editingExam ? 'PUT' : 'POST';

      const response = await fetch(endpoint, {
        method,
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders()
        },
        credentials: 'include',
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        toast.success(editingExam ? 'Mock exam updated' : 'Mock exam created');
        setDialogOpen(false);
        setEditingExam(null);
        resetForm();
        fetchMockExams();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to save exam');
      }
    } catch (error) {
      toast.error('Failed to save exam');
    }
  };

  const handleDeleteExam = async (mockExamId) => {
    if (!confirm('Are you sure you want to delete this mock exam?')) return;

    try {
      const response = await fetch(`${API}/admin/mock-exams/${mockExamId}`, {
        method: 'DELETE',
        headers: getAuthHeaders(),
        credentials: 'include'
      });

      if (response.ok) {
        toast.success('Mock exam deleted');
        fetchMockExams();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to delete exam');
      }
    } catch (error) {
      toast.error('Failed to delete exam');
    }
  };

  const handleToggleActive = async (exam) => {
    try {
      const response = await fetch(`${API}/admin/mock-exams/${exam.mock_exam_id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders()
        },
        credentials: 'include',
        body: JSON.stringify({ is_active: !exam.is_active })
      });

      if (response.ok) {
        toast.success(exam.is_active ? 'Exam deactivated' : 'Exam activated');
        fetchMockExams();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to update exam');
      }
    } catch (error) {
      toast.error('Failed to update exam');
    }
  };

  const openEditDialog = (exam) => {
    setEditingExam(exam);
    setFormData({
      track: exam.track,
      name: exam.name,
      description: exam.description || '',
      question_count: exam.question_count,
      time_minutes: exam.time_minutes,
      passing_score: exam.passing_score,
      is_active: exam.is_active
    });
    setDialogOpen(true);
  };

  const resetForm = () => {
    setFormData({
      track: 'PMP',
      name: '',
      description: '',
      question_count: 50,
      time_minutes: 60,
      passing_score: 61,
      is_active: true
    });
  };

  const handleTrackChange = (track) => {
    const trackConfig = tracks.find(t => t.code === track);
    if (trackConfig) {
      setFormData(prev => ({
        ...prev,
        track,
        question_count: trackConfig.defaults.questions,
        time_minutes: trackConfig.defaults.time,
        passing_score: trackConfig.defaults.passing
      }));
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="w-8 h-8 animate-spin text-[#0047AB]" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold flex items-center gap-2">
            <FileQuestion className="w-6 h-6 text-[#0047AB]" />
            Mock Exam Configuration
          </h2>
          <p className="text-slate-600 dark:text-slate-400 mt-1">
            Manage mock exams for each certification track (minimum 3 per track)
          </p>
        </div>
        
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button 
              className="bg-[#0047AB] hover:bg-[#003380]"
              onClick={() => { setEditingExam(null); resetForm(); }}
              data-testid="create-mock-exam-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Create Mock Exam
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>{editingExam ? 'Edit Mock Exam' : 'Create New Mock Exam'}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              <div>
                <Label>Track</Label>
                <Select 
                  value={formData.track} 
                  onValueChange={handleTrackChange}
                  disabled={!!editingExam}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {tracks.map(track => (
                      <SelectItem key={track.code} value={track.code}>
                        {track.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Name</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="e.g., PMP Mock Exam 1"
                />
              </div>

              <div>
                <Label>Description</Label>
                <Input
                  value={formData.description}
                  onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="Practice exam for PMP certification"
                />
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label>Questions</Label>
                  <Input
                    type="number"
                    value={formData.question_count}
                    onChange={(e) => setFormData(prev => ({ ...prev, question_count: parseInt(e.target.value) }))}
                  />
                </div>
                <div>
                  <Label>Time (min)</Label>
                  <Input
                    type="number"
                    value={formData.time_minutes}
                    onChange={(e) => setFormData(prev => ({ ...prev, time_minutes: parseInt(e.target.value) }))}
                  />
                </div>
                <div>
                  <Label>Pass %</Label>
                  <Input
                    type="number"
                    value={formData.passing_score}
                    onChange={(e) => setFormData(prev => ({ ...prev, passing_score: parseInt(e.target.value) }))}
                  />
                </div>
              </div>

              <div className="flex items-center justify-between">
                <Label>Active</Label>
                <Switch
                  checked={formData.is_active}
                  onCheckedChange={(checked) => setFormData(prev => ({ ...prev, is_active: checked }))}
                />
              </div>

              <Button 
                onClick={handleCreateExam} 
                className="w-full bg-[#0047AB] hover:bg-[#003380]"
              >
                {editingExam ? 'Update Mock Exam' : 'Create Mock Exam'}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {/* Track Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {tracks.map(track => {
          const summary = tracksSummary[track.code] || { total_exams: 0, active_exams: 0, total_questions_available: 0 };
          const hasMinimum = summary.active_exams >= 3;
          
          return (
            <Card key={track.code} className={`${hasMinimum ? 'border-green-200' : 'border-orange-200'}`}>
              <CardContent className="p-4">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-bold text-lg">{track.name}</h3>
                  {hasMinimum ? (
                    <CheckCircle className="w-5 h-5 text-green-600" />
                  ) : (
                    <AlertTriangle className="w-5 h-5 text-orange-600" />
                  )}
                </div>
                
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-slate-500">Active Exams:</span>
                    <span className={`font-bold ${hasMinimum ? 'text-green-600' : 'text-orange-600'}`}>
                      {summary.active_exams} / 3 min
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-500">Total Exams:</span>
                    <span>{summary.total_exams}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-500">Questions Available:</span>
                    <span>{summary.total_questions_available}</span>
                  </div>
                </div>

                {summary.total_exams === 0 && (
                  <Button
                    size="sm"
                    className="w-full mt-4 bg-[#0047AB] hover:bg-[#003380]"
                    onClick={() => handleGenerateExams(track.code, 3)}
                    disabled={generating[track.code] || summary.total_questions_available < 30}
                    data-testid={`generate-${track.code}-btn`}
                  >
                    {generating[track.code] ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Sparkles className="w-4 h-4 mr-2" />
                    )}
                    Generate 3 Exams
                  </Button>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Mock Exams List */}
      <Card>
        <CardHeader>
          <CardTitle>All Mock Exams</CardTitle>
        </CardHeader>
        <CardContent>
          {mockExams.length === 0 ? (
            <div className="text-center py-8 text-slate-500">
              <FileQuestion className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>No mock exams configured yet.</p>
              <p className="text-sm mt-2">Use the "Generate 3 Exams" buttons above to create initial mock exams for each track.</p>
            </div>
          ) : (
            <div className="space-y-3">
              {tracks.map(track => {
                const trackExams = mockExams.filter(e => e.track === track.code);
                if (trackExams.length === 0) return null;
                
                return (
                  <div key={track.code} className="border rounded-lg p-4">
                    <h4 className="font-semibold mb-3 flex items-center gap-2">
                      {track.name}
                      <Badge variant="outline">{trackExams.length} exams</Badge>
                    </h4>
                    <div className="space-y-2">
                      {trackExams.map(exam => (
                        <div 
                          key={exam.mock_exam_id}
                          className={`flex items-center justify-between p-3 rounded-lg ${
                            exam.is_active ? 'bg-slate-50 dark:bg-slate-800' : 'bg-slate-100 dark:bg-slate-900 opacity-60'
                          }`}
                        >
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <span className="font-medium">{exam.name}</span>
                              {!exam.is_active && (
                                <Badge variant="secondary" className="text-xs">Inactive</Badge>
                              )}
                            </div>
                            <div className="flex items-center gap-4 text-xs text-slate-500 mt-1">
                              <span className="flex items-center gap-1">
                                <FileQuestion className="w-3 h-3" />
                                {exam.question_count} questions
                              </span>
                              <span className="flex items-center gap-1">
                                <Clock className="w-3 h-3" />
                                {exam.time_minutes} min
                              </span>
                              <span className="flex items-center gap-1">
                                <Target className="w-3 h-3" />
                                {exam.passing_score}% to pass
                              </span>
                            </div>
                          </div>
                          
                          <div className="flex items-center gap-2">
                            <Switch
                              checked={exam.is_active}
                              onCheckedChange={() => handleToggleActive(exam)}
                            />
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => openEditDialog(exam)}
                            >
                              <Edit className="w-4 h-4" />
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              className="text-red-600 hover:text-red-700"
                              onClick={() => handleDeleteExam(exam.mock_exam_id)}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                    
                    {/* Add More Button */}
                    <Button
                      size="sm"
                      variant="outline"
                      className="mt-3"
                      onClick={() => handleGenerateExams(track.code, 1)}
                      disabled={generating[track.code]}
                    >
                      {generating[track.code] ? (
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <Plus className="w-4 h-4 mr-2" />
                      )}
                      Add Another Exam
                    </Button>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminMockExamsTab;
